import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { stringify } from "qs";
import type { Capabilities } from "src/contexts/CapabilitiesContext";
import { Query } from "src/types/query";
import axios from "src/utils/axios";

export const useQueryFeatureFlagsCapabilities = (props?: {
    posId?: number;
}): UseQueryResult<Capabilities, unknown> => {
    return useQuery({
        queryKey: [Query.FeatureFlags, "capabilities"],
        queryFn: async () => {
            const queryParams = !props?.posId
                ? ""
                : stringify({ posId: props?.posId }, { addQueryPrefix: true });
            return (await axios.get<Capabilities>(`feature-flags/capabilities${queryParams}`)).data;
        },
    });
};

export const useQueryFeatureFlagsCapabilitiesForOrganizations = (props?: {
    organizationIdList?: number[];
}): UseQueryResult<{ organizationId: number; capabilities: Capabilities }[], unknown> => {
    const normalizedOrganizationIds = (props?.organizationIdList ?? [])
        .slice()
        .sort((a, b) => a - b)
        .filter(
            (organizationId, index, array) =>
                organizationId > 0 && array.indexOf(organizationId) === index
        );

    return useQuery({
        queryKey: [
            Query.FeatureFlags,
            "capabilities",
            "organizations",
            normalizedOrganizationIds.join(","),
        ],
        enabled: normalizedOrganizationIds.length > 0,
        queryFn: async () =>
            (
                await axios.post<{ organizationId: number; capabilities: Capabilities }[]>(
                    "feature-flags/capabilities/organizations",
                    {
                        organizationIds: normalizedOrganizationIds,
                    }
                )
            ).data,
    });
};

export enum FeatureFlagKey {
    PARALLEL_OFFERS = "parallel-offers",
    PARALLEL_SPONSORED_ADS = "parallel-sponsored-ads",
    WELCOME_PAGE = "welcome-page",
    BULK_ACTIONS = "bulk-actions",
}

export type FeatureFlag = {
    id: number;
    key: FeatureFlagKey;
    enabled: boolean;
};

export const useQueryFeatureFlagKeys = (): UseQueryResult<
    Pick<FeatureFlag, "id" | "key">[],
    unknown
> =>
    useQuery({
        queryKey: [Query.FeatureFlags],
        queryFn: async () => (await axios.get(`feature-flags/keys`)).data,
    });
